/**
 * PLT-7096 — the review card of a supplier list uploaded in chat: every row of the file, in the
 * groups `review_supplier_import` reports. The user ticks the rows to create, picks or types the
 * websites the lookup could not settle, keeps the top rows by spend when the list is over the
 * allowance, and answers the currency question.
 *
 * The card is live, not a snapshot of the tool result: it reads the review from the card-state
 * routes and each change answers with the review as it then stands (`use-supplier-import-review.ts`).
 * It never groups or counts rows towards the allowance itself, so what it shows is what the agent
 * reads with `get_supplier_import`.
 *
 * PLT-7097: Create sends the ticked ready rows to be created in the background, a few at a time. The
 * card follows them from "Being created" into "Created" or "Not created", and says why for each one
 * that was not. Only the user creates, from here: the agent cannot.
 *
 * PLT-7100: each created supplier is then followed to assessed, with the status the Suppliers list
 * gives it, and what went wrong on our side — a row whose creation failed, a supplier whose products
 * could not be started, or whose modelling or assessment failed — can be tried again from here. The
 * rows that are not suppliers download as a CSV with their reasons, which the summary posted in chat
 * when the import is done points to.
 */
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  SupplierImportCreationIssue,
  SupplierImportReviewGroup,
  SupplierImportReviewNoDataPayload,
  SupplierImportReviewRow,
  SupplierImportReviewSuccessPayload,
  SupplierImportRowChange,
  SupplierImportSelection,
  SupplierImportWebsiteRefusal,
} from '@upright/common/mcp-payloads/supplier-import-review';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { Button } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Checkbox } from '@upright/ui/common/generic/components/checkbox';
import { Input } from '@upright/ui/common/generic/components/input';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Link } from '@upright/ui/common/generic/components/link';
import { Radio, RadioGroup } from '@upright/ui/common/generic/components/radio';
import { Spinner } from '@upright/ui/common/generic/components/spinner';
import { StateMessage } from '@upright/ui/common/generic/components/state-message';
import { Heading, Text } from '@upright/ui/common/generic/components/typography';
import { VisuallyHidden } from '@upright/ui/common/generic/components/visually-hidden';
import { saveAs } from '@upright/ui/common/generic/utilities/save-as';
import { STATUS_LABELS } from '@upright/ui/views/dma-v2-view/dashboard/suppliers/status-labels';
import { formatSpendAmount } from '@upright/ui/views/dma-v2-view/dashboard/suppliers/supplier-spend';
import { DisclosureChevron } from '@upright/ui/views/dma-v2-view/dashboard/suppliers/supplier-risk-takeaway';
import {
  getSupplierImportReviewQuery,
  useAnswerSupplierImportCurrencyMutation,
  useChangeSupplierImportRowMutation,
  useCreateSupplierImportSuppliersMutation,
  useRetrySupplierImportRowsMutation,
  useSelectSupplierImportRowsMutation,
  type AssessmentMovement,
} from './use-supplier-import-review';
import { buildNotCreatedCsv, notCreatedCsvFilename } from './supplier-import-not-created-csv';
import styles from './supplier-import-review-card.module.css';

/** A website the user gave that was not taken, kept so the field can say why while it still holds it. */
export interface RefusedWebsite {
  website: string;
  refusal: SupplierImportWebsiteRefusal;
}

/** As the server orders them: what is happening and what went wrong first, what is done last. */
const GROUP_ORDER: SupplierImportReviewGroup[] = [
  'creating',
  'not-created',
  'ready',
  'pick-website',
  'no-website',
  'awaiting-website',
  'already-a-supplier',
  'not-usable',
  'created',
];

const GROUP_LABELS: Record<SupplierImportReviewGroup, string> = {
  creating: 'Being created',
  'not-created': 'Not created',
  ready: 'Ready to create',
  'pick-website': 'Pick a website',
  'no-website': 'No website found',
  'awaiting-website': 'Looking up a website',
  'already-a-supplier': 'Already a supplier',
  'not-usable': 'Cannot be created',
  created: 'Created',
};

/** Groups that are suppliers or can become ones, so their spend is worth showing. */
const SPEND_SHOWN_GROUPS = new Set<SupplierImportReviewGroup>([
  'creating',
  'created',
  'ready',
  'pick-website',
  'no-website',
  'awaiting-website',
]);

/** A longer list starts closed, so the rows that need the user are not pushed off the card. */
const OPEN_UP_TO = 20;

const CHANGE_NOT_SAVED = 'The change could not be saved. Try again.';

/** With no lookup running, a row waiting for one waits for the user instead. */
function groupLabel(group: SupplierImportReviewGroup, websiteLookupRunning: boolean): string {
  return group === 'awaiting-website' && !websiteLookupRunning ? 'Website not looked up' : GROUP_LABELS[group];
}

function isOpenByDefault(group: SupplierImportReviewGroup, count: number, websiteLookupRunning: boolean): boolean {
  switch (group) {
    case 'not-created':
    case 'pick-website':
    case 'no-website':
      return true;
    case 'awaiting-website':
      return !websiteLookupRunning;
    case 'creating':
    case 'ready':
    case 'created':
      return count <= OPEN_UP_TO;
    case 'already-a-supplier':
    case 'not-usable':
      return false;
  }
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function websiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

/** The website as a reader names it: no protocol, no trailing slash. */
function websiteLabel(website: string): string {
  return website.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function rowLabel(row: SupplierImportReviewRow): string {
  return row.name ?? `Row ${row.rowNumber}`;
}

function refusalMessage(refusal: SupplierImportWebsiteRefusal): string {
  switch (refusal.reason) {
    case 'not-a-website':
      return 'That is not a website address.';
    case 'does-not-answer':
      return 'That website does not answer.';
    case 'existing-supplier':
      return refusal.companyName
        ? `Already on your supplier list as ${refusal.companyName}.`
        : 'A supplier on your list already has this website.';
    case 'other-row':
      return `${refusal.name ?? `Row ${refusal.rowNumber}`} (row ${refusal.rowNumber}) already has this website.`;
  }
}

/** Undoing the user's website brings back the file's or the lookup's, which another supplier may now have. */
function undoRefusalMessage(refusal: SupplierImportWebsiteRefusal): string {
  if (refusal.reason === 'existing-supplier') {
    return refusal.companyName
      ? `Cannot undo: the earlier website is already on your supplier list as ${refusal.companyName}.`
      : 'Cannot undo: a supplier on your list already has the earlier website.';
  }
  if (refusal.reason === 'other-row') {
    return `Cannot undo: ${refusal.name ?? `Row ${refusal.rowNumber}`} (row ${refusal.rowNumber}) already has the earlier website.`;
  }
  return CHANGE_NOT_SAVED;
}

/** For a row in the `no-website` group: website-not-found, website-lookup-failed or website-not-usable. */
function noWebsiteReason(row: SupplierImportReviewRow): string {
  if (row.bucket === 'website-lookup-failed') {
    return 'The search for a website could not run. Add one to create this supplier.';
  }
  if (row.bucket === 'website-not-usable') {
    return row.website
      ? `${websiteLabel(row.website)} does not answer. Add another to create this supplier.`
      : 'The website in the file does not answer. Add another to create this supplier.';
  }
  return 'No website found. Add one to create this supplier.';
}

/** For a row in the `not-usable` group: duplicate-in-file, internal-id-not-usable or unusable. */
function notUsableReason(row: SupplierImportReviewRow, rowsByNumber: Map<number, SupplierImportReviewRow>): string {
  if (row.bucket === 'duplicate-in-file') {
    const original = row.duplicateOfRowNumber === null ? undefined : rowsByNumber.get(row.duplicateOfRowNumber);
    return original ? `Repeats ${rowLabel(original)}, row ${original.rowNumber}.` : 'Repeats an earlier row.';
  }
  if (row.bucket === 'internal-id-not-usable') {
    return row.internalIdIssue === 'too-long'
      ? 'The internal ID is longer than an internal ID can be.'
      : internalIdTakenMessage(row);
  }
  return row.name === null ? 'No supplier name.' : 'A totals line, not a supplier.';
}

function internalIdTakenMessage(row: SupplierImportReviewRow): string {
  return `Internal ID ${row.internalId ?? ''} is already used by another company.`;
}

/** For a row in the `not-created` group: what changed between sorting and creating it. */
function notCreatedReason(row: SupplierImportReviewRow, buyerCompanyName: string): string {
  const issue: SupplierImportCreationIssue | null = row.creationIssue;
  switch (issue) {
    case 'already-a-supplier':
      return row.existingSupplier?.companyName
        ? `Already on your supplier list as ${row.existingSupplier.companyName}.`
        : 'Already on your supplier list.';
    case 'internal-id-taken':
      return internalIdTakenMessage(row);
    case 'supplier-is-buyer':
      return `The website is ${buyerCompanyName}’s own.`;
    case 'allowance-used-up':
      return `${buyerCompanyName}’s allowance ran out first: suppliers were added another way meanwhile.`;
    case 'website-not-usable':
      return row.website ? `${websiteLabel(row.website)} stopped answering.` : 'The website stopped answering.';
    case 'buyer-unavailable':
      return `${buyerCompanyName}’s suppliers can no longer be added from your account.`;
    case 'failed':
    case 'products-not-started':
    case null:
      return 'Something went wrong on our side.';
  }
}

function alreadyASupplierReason(row: SupplierImportReviewRow): string {
  return row.existingSupplier?.companyName
    ? `Already on your supplier list as ${row.existingSupplier.companyName}.`
    : 'Already on your supplier list.';
}

/**
 * PLT-7100: why a row is not a supplier, in the words its row in the card uses, for the download of
 * the rows not created. Null for a row created or on its way.
 */
function rowNotCreatedReason(
  row: SupplierImportReviewRow,
  review: SupplierImportReviewSuccessPayload,
  rowsByNumber: Map<number, SupplierImportReviewRow>,
): string | null {
  switch (row.group) {
    case 'creating':
    case 'created':
      return null;
    case 'not-created':
      return notCreatedReason(row, review.buyerCompanyName);
    case 'ready':
      return row.included ? 'Ready, not created yet.' : 'Ready, but not ticked.';
    case 'pick-website':
      return 'Several websites were found. Pick one to create this supplier.';
    case 'no-website':
      return noWebsiteReason(row);
    case 'awaiting-website':
      return review.websiteLookupRunning
        ? 'Its website is still being looked up.'
        : 'Not looked up yet. Add a website to create this supplier.';
    case 'already-a-supplier':
      return alreadyASupplierReason(row);
    case 'not-usable':
      return notUsableReason(row, rowsByNumber);
  }
}

/** PLT-7100: what went wrong on our side, which "Try again" tries again. */
function isRetryable(row: SupplierImportReviewRow): boolean {
  if (row.group === 'not-created') return row.creationIssue === 'failed';
  return (
    row.group === 'created' &&
    (row.creationIssue === 'products-not-started' ||
      row.assessmentStatus === 'modelling-failed' ||
      row.assessmentStatus === 'assessment-failed')
  );
}

/** For a created row `isRetryable` holds for. */
function createdIssueReason(row: SupplierImportReviewRow): string {
  if (row.creationIssue === 'products-not-started' || row.assessmentStatus === null) {
    return 'Added to your supplier list, but working out its products could not be started.';
  }
  return `${STATUS_LABELS[row.assessmentStatus]}.`;
}

const WEBSITE_SOURCE_LABELS: Record<NonNullable<SupplierImportReviewRow['websiteSource']>, string> = {
  file: 'From the file',
  lookup: 'Found by search',
  user: 'Chosen by you',
};

// ---------------------------------------------------------------------------
// The container: data, the user's changes in flight, and the states before there is a review.

export function SupplierImportReviewCard({ importId }: { importId: string }) {
  const [shownAt] = useState(() => Date.now());
  // The poll mutates it, to time a stalled assessment across polls. Its clock starts at the first
  // statuses it reads.
  const movement = useRef<AssessmentMovement>({ statuses: '', at: 0 });
  const reviewQuery = useQuery(getSupplierImportReviewQuery(importId, shownAt, movement.current));
  const changeRowMutation = useChangeSupplierImportRowMutation(importId);
  const selectRowsMutation = useSelectSupplierImportRowsMutation(importId);
  const answerCurrencyMutation = useAnswerSupplierImportCurrencyMutation(importId);
  const createMutation = useCreateSupplierImportSuppliersMutation(importId);
  const retryMutation = useRetrySupplierImportRowsMutation(importId);
  const [refusedWebsites, setRefusedWebsites] = useState<ReadonlyMap<number, RefusedWebsite>>(() => new Map());
  const [pendingRows, setPendingRows] = useState<ReadonlyMap<number, SupplierImportRowChange>>(() => new Map());
  const [changeError, setChangeError] = useState<string | null>(null);

  async function changeRow(rowNumber: number, change: SupplierImportRowChange): Promise<boolean> {
    setPendingRows(rows => new Map(rows).set(rowNumber, change));
    setChangeError(null);
    try {
      const update = await changeRowMutation.mutateAsync({ rowNumber, change });
      const refusal = update.websiteRefused;
      // An Undo has no field to show its refusal under.
      if (refusal !== undefined && change.website === null) setChangeError(undoRefusalMessage(refusal));
      setRefusedWebsites(refused => {
        const next = new Map(refused);
        if (refusal !== undefined && typeof change.website === 'string') {
          next.set(rowNumber, { website: change.website, refusal });
        } else {
          next.delete(rowNumber);
        }
        return next;
      });
      return refusal === undefined;
    } catch {
      setChangeError(CHANGE_NOT_SAVED);
      return false;
    } finally {
      setPendingRows(rows => {
        // A later change to the same row is still on its way: leave its marker.
        if (rows.get(rowNumber) !== change) return rows;
        const next = new Map(rows);
        next.delete(rowNumber);
        return next;
      });
    }
  }

  async function select(selection: SupplierImportSelection) {
    setChangeError(null);
    try {
      await selectRowsMutation.mutateAsync(selection);
    } catch {
      setChangeError(CHANGE_NOT_SAVED);
    }
  }

  async function answerCurrency(inBuyerCurrency: boolean) {
    setChangeError(null);
    try {
      await answerCurrencyMutation.mutateAsync({ inBuyerCurrency });
    } catch {
      setChangeError('The answer could not be saved. Try again.');
    }
  }

  async function create(rowNumbers: number[]) {
    setChangeError(null);
    try {
      await createMutation.mutateAsync({ rowNumbers });
    } catch (error) {
      // A conflict says what changed since the card was drawn, in words for the user; the review is
      // read again, so the card shows what it is now.
      setChangeError(
        error instanceof ErrorWithHttpStatus && error.status === 409
          ? error.message
          : 'The suppliers could not be sent for creation. Try again.',
      );
    }
  }

  async function retry(rowNumbers: number[]) {
    setChangeError(null);
    try {
      await retryMutation.mutateAsync({ rowNumbers });
    } catch (error) {
      // A conflict is the allowance, in words for the user.
      setChangeError(
        error instanceof ErrorWithHttpStatus && error.status === 409
          ? error.message
          : 'The suppliers could not be tried again. Try again later.',
      );
    }
  }

  const expired = reviewQuery.error instanceof ErrorWithHttpStatus && reviewQuery.error.status === 410;
  if (expired) return <SupplierImportReviewExpired />;
  if (reviewQuery.isPending) return <SupplierImportReviewLoading />;
  // A failed poll keeps the review it already has on screen; the next poll tries again.
  if (reviewQuery.isError && reviewQuery.data === undefined) {
    return (
      <SupplierImportReviewFailed
        onRetry={() => {
          void reviewQuery.refetch();
        }}
      />
    );
  }
  const review = reviewQuery.data;
  if ('dataAvailable' in review) return <SupplierImportReviewUnavailable review={review} />;
  return (
    <SupplierImportReviewView
      review={review}
      refusedWebsites={refusedWebsites}
      pendingRows={pendingRows}
      isSelecting={selectRowsMutation.isPending}
      isAnsweringCurrency={answerCurrencyMutation.isPending}
      isCreating={createMutation.isPending}
      isRetrying={retryMutation.isPending}
      changeError={changeError}
      onChangeRow={changeRow}
      onSelect={selection => {
        void select(selection);
      }}
      onAnswerCurrency={inBuyerCurrency => {
        void answerCurrency(inBuyerCurrency);
      }}
      onCreate={rowNumbers => {
        void create(rowNumbers);
      }}
      onRetry={rowNumbers => {
        void retry(rowNumbers);
      }}
    />
  );
}

export function SupplierImportReviewLoading() {
  return (
    <DataCard>
      <Stack row gap="sm" align="center">
        <Spinner aria-label="Loading the supplier list" />
        <Text size="sm" color="gray-600">
          Loading the supplier list…
        </Text>
      </Stack>
    </DataCard>
  );
}

export function SupplierImportReviewExpired() {
  return (
    <DataCard>
      <StateMessage
        layout="section"
        headingLevel={3}
        title="This supplier list has expired"
        description="Upload the file again to review it."
      />
    </DataCard>
  );
}

export function SupplierImportReviewFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <DataCard>
      <StateMessage
        layout="section"
        headingLevel={3}
        title="The supplier list could not be loaded"
        description="Something went wrong while reading it."
        primaryAction={
          <Button variant="outlined" size="small" onPress={onRetry}>
            Try again
          </Button>
        }
      />
    </DataCard>
  );
}

export function SupplierImportReviewUnavailable({ review }: { review: SupplierImportReviewNoDataPayload }) {
  return (
    <DataCard>
      <StateMessage
        layout="section"
        headingLevel={3}
        title="This supplier list cannot be reviewed"
        description={review.reason === 'not-prepared' ? 'The list has not been sorted yet.' : review.suggestion}
      />
    </DataCard>
  );
}

// ---------------------------------------------------------------------------
// The review itself, presentational, so the prototype renders the shipped card.

export interface SupplierImportReviewViewProps {
  review: SupplierImportReviewSuccessPayload;
  refusedWebsites: ReadonlyMap<number, RefusedWebsite>;
  /** Rows with a change on its way, and the change. */
  pendingRows: ReadonlyMap<number, SupplierImportRowChange>;
  isSelecting: boolean;
  isAnsweringCurrency: boolean;
  isCreating: boolean;
  isRetrying: boolean;
  changeError: string | null;
  /** Resolves true when the change was saved; false when a website was refused or the change failed. */
  onChangeRow: (rowNumber: number, change: SupplierImportRowChange) => Promise<boolean>;
  onSelect: (selection: SupplierImportSelection) => void;
  onAnswerCurrency: (inBuyerCurrency: boolean) => void;
  /** The rows the card shows ticked and ready: the server refuses them all if one no longer is. */
  onCreate: (rowNumbers: number[]) => void;
  /** Rows whose creation went wrong on our side (`isRetryable`): created again, or their products started. */
  onRetry: (rowNumbers: number[]) => void;
}

export function SupplierImportReviewView(props: SupplierImportReviewViewProps) {
  const { review, changeError } = props;
  const [toggledGroups, setToggledGroups] = useState<Partial<Record<SupplierImportReviewGroup, boolean>>>({});
  const rowsByNumber = new Map(review.rows.map(row => [row.rowNumber, row]));
  const subtitle = [review.buyerCompanyName, review.filename, plural(review.rows.length, 'row', 'rows')]
    .filter(item => item !== null && item !== '')
    .join(' · ');
  const spendLeftOut = review.rows.filter(
    row =>
      SPEND_SHOWN_GROUPS.has(row.group) &&
      row.spendIssue !== null &&
      !(review.needsCurrencyConfirmation && row.spendIssue === 'currency-not-stated'),
  ).length;

  return (
    <DataCard>
      <Stack gap="xl">
        <Stack gap="md">
          <Heading level={3} size="lg" weight="bold" color="gray-900">
            Review the supplier list
          </Heading>
          <Text size="sm" color="gray-600">
            {subtitle}
          </Text>
        </Stack>

        <Stack gap="lg">
          <AllowanceSummary {...props} />
          {review.needsCurrencyConfirmation && <CurrencyQuestion {...props} />}
          <CreateAction {...props} />
          {review.creation.queued > 0 && (
            <Stack row gap="sm" align="center">
              <Spinner aria-label="Creating suppliers" />
              <Text size="sm" color="gray-600">
                Creating {plural(review.creation.queued, 'supplier', 'suppliers')}…
              </Text>
            </Stack>
          )}
          <AssessmentProgress {...props} />
          <NotCreatedDownload review={review} rowsByNumber={rowsByNumber} />
          {review.websiteLookupRunning && review.counts['awaiting-website'] > 0 && (
            <Stack row gap="sm" align="center">
              <Spinner aria-label="Looking up websites" />
              <Text size="sm" color="gray-600">
                Looking up websites for {plural(review.counts['awaiting-website'], 'row', 'rows')}…
              </Text>
            </Stack>
          )}
          {changeError !== null && (
            <div role="alert">
              <Text size="sm" color="gray-900">
                {changeError}
              </Text>
            </div>
          )}

          <div>
            <div className={styles.columns}>
              <span />
              <Text size="sm" weight="medium" color="gray-600">
                Supplier
              </Text>
              <Text size="sm" weight="medium" color="gray-600" align="end">
                Annual spend ({review.spendCurrency})
              </Text>
            </div>
            {GROUP_ORDER.filter(group => review.counts[group] > 0).map(group => {
              const rows = review.rows.filter(row => row.group === group);
              const isOpen = toggledGroups[group] ?? isOpenByDefault(group, rows.length, review.websiteLookupRunning);
              return (
                <ReviewGroup
                  key={group}
                  label={groupLabel(group, review.websiteLookupRunning)}
                  count={rows.length}
                  isOpen={isOpen}
                  onToggle={() => {
                    setToggledGroups(toggled => ({ ...toggled, [group]: !isOpen }));
                  }}
                >
                  {group === 'ready' && rows.length > 1 && <SelectAllRow {...props} rows={rows} />}
                  {rows.map(row => (
                    <ReviewRow key={row.rowNumber} {...props} row={row} rowsByNumber={rowsByNumber} />
                  ))}
                </ReviewGroup>
              );
            })}
          </div>

          {spendLeftOut > 0 && (
            <Text size="sm" color="gray-600">
              Spend is left out for {plural(spendLeftOut, 'row', 'rows')}: it could not be read, or is in another
              currency than {review.spendCurrency}. Amounts are never converted.
            </Text>
          )}
        </Stack>
      </Stack>
    </DataCard>
  );
}

/** `allowanceLeft`, not the allowance's `remaining`: the rows still being created will spend theirs. */
function AllowanceSummary({ review, isSelecting, onSelect }: SupplierImportReviewViewProps) {
  const { allowance, allowanceLeft, toCreate } = review;
  const overBy = toCreate - allowanceLeft;
  // Not before the currency question is answered: spend stating no currency is left out until then,
  // so those rows would rank last whatever their amount.
  const canKeepTopBySpend =
    !review.needsCurrencyConfirmation && review.rows.some(row => row.group === 'ready' && row.spendAmount !== null);
  return (
    <Stack gap="md">
      <Text color="gray-900">
        {plural(toCreate, 'supplier', 'suppliers')} to create · {allowanceLeft} of {allowance.limit} left in{' '}
        {review.buyerCompanyName}&rsquo;s allowance
      </Text>
      {/* Ticked or not: with none left, ticking a row only takes the user over the allowance. */}
      {allowanceLeft === 0 && review.counts.ready > 0 && (
        <Text size="sm" color="gray-600">
          {review.creation.queued > 0
            ? 'The suppliers being created take the rest of the allowance, so none of these can be created.'
            : 'The allowance is used up, so none of these can be created.'}
        </Text>
      )}
      {review.overAllowance && allowanceLeft > 0 && (
        <Stack gap="md" align="start">
          <Text size="sm" color="gray-600">
            That is {overBy} more than the allowance has left.{' '}
            {canKeepTopBySpend
              ? `Untick rows, or keep the top ${allowanceLeft} by spend.`
              : review.needsCurrencyConfirmation
                ? `Untick rows until ${allowanceLeft} are left, or answer the currency question to keep the top ${allowanceLeft} by spend.`
                : `Untick rows until ${allowanceLeft} are left.`}
          </Text>
          {canKeepTopBySpend && (
            <Button
              variant="outlined"
              size="small"
              isPending={isSelecting}
              onPress={() => {
                onSelect({ topBySpend: allowanceLeft });
              }}
            >
              Keep the top {allowanceLeft} by spend
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Create, for the rows the card shows ticked and ready. Held back while a change is on its way, so
 * what is sent is what the user sees; over the allowance and before the currency question is
 * answered the server would refuse it, so the card does not offer it.
 */
function CreateAction({
  review,
  pendingRows,
  isSelecting,
  isAnsweringCurrency,
  isCreating,
  onCreate,
}: SupplierImportReviewViewProps) {
  if (review.counts.ready === 0) return null;
  const rowNumbers = review.rows.filter(row => row.group === 'ready' && row.included).map(row => row.rowNumber);
  const isChanging = pendingRows.size > 0 || isSelecting || isAnsweringCurrency;
  return (
    <Stack row gap="md" align="center" wrap>
      <Button
        variant="contained"
        size="small"
        isPending={isCreating}
        isDisabled={review.toCreate === 0 || review.overAllowance || review.needsCurrencyConfirmation || isChanging}
        onPress={() => {
          onCreate(rowNumbers);
        }}
      >
        {review.toCreate === 0 ? 'Create suppliers' : `Create ${plural(review.toCreate, 'supplier', 'suppliers')}`}
      </Button>
      {review.needsCurrencyConfirmation && (
        <Text size="sm" color="gray-600">
          Answer the currency question first.
        </Text>
      )}
      {review.toCreate === 0 && !review.needsCurrencyConfirmation && review.allowanceLeft > 0 && (
        <Text size="sm" color="gray-600">
          Tick the suppliers to create.
        </Text>
      )}
    </Stack>
  );
}

/**
 * PLT-7100: the created suppliers on their way to assessed, in the Suppliers list's words, and one
 * "Try again" for everything that went wrong on our side when there is more than one.
 */
function AssessmentProgress({ review, isRetrying, onRetry }: SupplierImportReviewViewProps) {
  const retryable = review.rows.filter(isRetryable).map(row => row.rowNumber);
  const { created } = review.creation;
  const { inProgress, assessed, toReview, failed } = review.assessment;
  const parts = [
    `${plural(created, 'supplier', 'suppliers')} created`,
    assessed > 0 ? `${assessed} assessed` : null,
    inProgress > 0 ? `${inProgress} being assessed` : null,
    toReview > 0 ? `${toReview} with products to review` : null,
    failed > 0 ? `${failed} failed` : null,
  ].filter(part => part !== null);
  return (
    <>
      {created > 0 && (
        <Stack row gap="sm" align="center">
          {inProgress > 0 && <Spinner aria-label="Assessing suppliers" />}
          <Text size="sm" color="gray-600">
            {parts.join(' · ')}
          </Text>
        </Stack>
      )}
      {retryable.length > 1 && (
        <Stack row gap="md" align="center" wrap>
          <Text size="sm" color="gray-600">
            Something went wrong on our side with {plural(retryable.length, 'supplier', 'suppliers')}.
          </Text>
          <Button
            variant="outlined"
            size="small"
            isPending={isRetrying}
            onPress={() => {
              onRetry(retryable);
            }}
          >
            Try them again
          </Button>
        </Stack>
      )}
    </>
  );
}

/**
 * PLT-7100: once anything has been created from the list, the rows that are not suppliers, each with
 * its reason, as a CSV: to fix in the file, or to hand to whoever looks the websites up. The summary
 * posted in chat points here.
 */
function NotCreatedDownload({
  review,
  rowsByNumber,
}: {
  review: SupplierImportReviewSuccessPayload;
  rowsByNumber: Map<number, SupplierImportReviewRow>;
}) {
  if (review.creation.created + review.creation.notCreated === 0) return null;
  const notCreated = review.rows.flatMap(row => {
    const reason = rowNotCreatedReason(row, review, rowsByNumber);
    return reason === null ? [] : [{ row, reason }];
  });
  if (notCreated.length === 0) return null;
  return (
    <Stack row gap="sm" align="center" wrap>
      <Text size="sm" color="gray-600">
        {plural(notCreated.length, 'row', 'rows')} of the list {notCreated.length === 1 ? 'is' : 'are'} not a supplier.
      </Text>
      <Button
        variant="text"
        size="small"
        onPress={() => {
          // With a byte order mark, so a spreadsheet reads the names as UTF-8 rather than mangling "ä".
          saveAs(
            new Blob(['﻿', buildNotCreatedCsv(notCreated, review.spendCurrency)], {
              type: 'text/csv;charset=utf-8',
            }),
            notCreatedCsvFilename(review.filename),
          );
        }}
      >
        Download them as CSV
      </Button>
    </Stack>
  );
}

/** A row's reason, and "Try again" for it. */
function RetryableDetail({
  row,
  reason,
  isRetrying,
  onRetry,
}: SupplierImportReviewViewProps & { row: SupplierImportReviewRow; reason: string }) {
  return (
    <Stack row gap="sm" align="center" wrap>
      <Text size="sm" color="gray-600">
        {reason}
      </Text>
      <Button
        variant="text"
        size="small"
        isDisabled={isRetrying}
        onPress={() => {
          onRetry([row.rowNumber]);
        }}
      >
        Try again
      </Button>
    </Stack>
  );
}

function CurrencyQuestion({ review, isAnsweringCurrency, onAnswerCurrency }: SupplierImportReviewViewProps) {
  const currency = review.spendCurrency;
  return (
    <Stack gap="md" align="start">
      <Text color="gray-900">
        {plural(review.rowsWithoutCurrency, 'row states its', 'rows state their')} spend with no currency. Is it in{' '}
        {currency}?
      </Text>
      <Stack row gap="sm">
        <Button
          variant="outlined"
          size="small"
          isDisabled={isAnsweringCurrency}
          onPress={() => {
            onAnswerCurrency(false);
          }}
        >
          No, leave it out
        </Button>
        <Button
          variant="outlined"
          size="small"
          isDisabled={isAnsweringCurrency}
          onPress={() => {
            onAnswerCurrency(true);
          }}
        >
          Yes, it is in {currency}
        </Button>
      </Stack>
    </Stack>
  );
}

function ReviewGroup({
  label,
  count,
  isOpen,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();
  return (
    <section className={styles.group}>
      <button
        type="button"
        className={styles.groupToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className={styles.disclosure} aria-hidden={true}>
          <DisclosureChevron />
        </span>
        <Text weight="semibold" color="gray-900">
          {label}
        </Text>
        <Text color="gray-600" align="end">
          {count}
        </Text>
      </button>
      {isOpen && (
        <ul id={panelId} className={styles.rows}>
          {children}
        </ul>
      )}
    </section>
  );
}

function SelectAllRow({
  rows,
  isSelecting,
  onSelect,
}: SupplierImportReviewViewProps & { rows: SupplierImportReviewRow[] }) {
  const ticked = rows.filter(row => row.included).length;
  return (
    <li className={styles.row}>
      <Checkbox
        isSelected={ticked === rows.length}
        isIndeterminate={ticked > 0 && ticked < rows.length}
        isDisabled={isSelecting}
        onChange={() => {
          onSelect({ included: ticked < rows.length });
        }}
      >
        <VisuallyHidden>Select all</VisuallyHidden>
      </Checkbox>
      <Text size="sm" color="gray-600">
        Select all
      </Text>
    </li>
  );
}

function ReviewRow(
  props: SupplierImportReviewViewProps & {
    row: SupplierImportReviewRow;
    rowsByNumber: Map<number, SupplierImportReviewRow>;
  },
) {
  const { row, pendingRows, onChangeRow } = props;
  const pending = pendingRows.get(row.rowNumber);
  return (
    <li className={styles.row}>
      {row.group === 'ready' ? (
        <Checkbox
          isSelected={pending?.included ?? row.included}
          isDisabled={pending !== undefined}
          onChange={selected => {
            void onChangeRow(row.rowNumber, { included: selected });
          }}
        >
          <VisuallyHidden>Create {rowLabel(row)}</VisuallyHidden>
        </Checkbox>
      ) : (
        <span />
      )}
      <div className={styles.main}>
        {row.name === null ? (
          <Text color="gray-600">No name</Text>
        ) : (
          <Text weight="medium" color="gray-900" truncate>
            {row.name}
          </Text>
        )}
        <RowDetail {...props} />
      </div>
      <span className={styles.spend}>
        {SPEND_SHOWN_GROUPS.has(row.group) &&
          (row.spendAmount === null ? (
            <Text size="sm" color="gray-600">
              —
            </Text>
          ) : (
            <Text size="sm" color="gray-900">
              {formatSpendAmount(row.spendAmount)}
            </Text>
          ))}
      </span>
    </li>
  );
}

function RowDetail(
  props: SupplierImportReviewViewProps & {
    row: SupplierImportReviewRow;
    rowsByNumber: Map<number, SupplierImportReviewRow>;
  },
) {
  const { row, rowsByNumber } = props;
  switch (row.group) {
    case 'creating':
      return <SettledWebsite row={row} />;
    case 'created':
      return (
        <Stack gap="sm">
          <SettledWebsite row={row} />
          {isRetryable(row) ? (
            <RetryableDetail {...props} reason={createdIssueReason(row)} />
          ) : (
            <Text size="sm" color="gray-600">
              {/* As the Suppliers list says it, so the two read the same. */}
              {row.assessmentStatus === null ? 'Added to your supplier list.' : STATUS_LABELS[row.assessmentStatus]}
            </Text>
          )}
        </Stack>
      );
    case 'not-created':
      return isRetryable(row) ? (
        <RetryableDetail {...props} reason={notCreatedReason(row, props.review.buyerCompanyName)} />
      ) : (
        <Text size="sm" color="gray-600">
          {notCreatedReason(row, props.review.buyerCompanyName)}
        </Text>
      );
    case 'ready':
      return <ReadyWebsite {...props} />;
    case 'pick-website':
      return <PickWebsite {...props} />;
    case 'no-website':
      return (
        <Stack gap="sm">
          <Text size="sm" color="gray-600">
            {noWebsiteReason(row)}
          </Text>
          <TypedWebsite {...props} submitLabel="Add website" />
        </Stack>
      );
    case 'awaiting-website':
      return props.review.websiteLookupRunning ? (
        <Text size="sm" color="gray-600">
          Looking up a website…
        </Text>
      ) : (
        <Stack gap="sm">
          <Text size="sm" color="gray-600">
            Not looked up yet. Add a website to create this supplier.
          </Text>
          <TypedWebsite {...props} submitLabel="Add website" />
        </Stack>
      );
    case 'already-a-supplier':
      return (
        <Text size="sm" color="gray-600">
          {alreadyASupplierReason(row)}
        </Text>
      );
    case 'not-usable':
      return (
        <Text size="sm" color="gray-600">
          {notUsableReason(row, rowsByNumber)}
        </Text>
      );
  }
}

/** The website a row sent for creation has: settled, so nothing to change or undo. */
function SettledWebsite({ row }: { row: SupplierImportReviewRow }) {
  if (row.website === null) return null;
  return (
    <Text size="sm" truncate>
      <Link href={websiteHref(row.website)}>{websiteLabel(row.website)}</Link>
    </Text>
  );
}

function ReadyWebsite(props: SupplierImportReviewViewProps & { row: SupplierImportReviewRow }) {
  const { row, pendingRows, onChangeRow } = props;
  const [isEditing, setIsEditing] = useState(false);
  const isPending = pendingRows.has(row.rowNumber);
  // A ready row always has one; the guard is for the compiler.
  if (row.website === null) return null;
  if (isEditing) {
    return (
      <TypedWebsite
        {...props}
        submitLabel="Save"
        initialWebsite={websiteLabel(row.website)}
        onCancel={() => {
          setIsEditing(false);
        }}
        onSaved={() => {
          setIsEditing(false);
        }}
      />
    );
  }
  return (
    <Stack row gap="sm" align="baseline" wrap>
      <Text size="sm" truncate>
        <Link href={websiteHref(row.website)}>{websiteLabel(row.website)}</Link>
      </Text>
      {row.websiteSource !== null && (
        <Text size="sm" color="gray-600">
          {WEBSITE_SOURCE_LABELS[row.websiteSource]}
        </Text>
      )}
      <Text size="sm">
        <Link
          isDisabled={isPending}
          onPress={() => {
            setIsEditing(true);
          }}
        >
          Change
        </Link>
      </Text>
      {row.websiteSource === 'user' && (
        <Text size="sm">
          <Link
            isDisabled={isPending}
            onPress={() => {
              void onChangeRow(row.rowNumber, { website: null });
            }}
          >
            Undo
          </Link>
        </Text>
      )}
    </Stack>
  );
}

function PickWebsite(props: SupplierImportReviewViewProps & { row: SupplierImportReviewRow }) {
  const { row, refusedWebsites, pendingRows, onChangeRow } = props;
  const [isTyping, setIsTyping] = useState(false);
  const pending = pendingRows.get(row.rowNumber);
  const refused = refusedWebsites.get(row.rowNumber);
  // A candidate picked and refused: the radio goes back to none, so the reason is said beside it.
  const refusedPick = row.websiteCandidates.some(candidate => candidate.website === refused?.website)
    ? refused
    : undefined;
  return (
    <Stack gap="sm">
      <RadioGroup
        aria-label={`Website for ${rowLabel(row)}`}
        value={typeof pending?.website === 'string' ? pending.website : null}
        isDisabled={pending !== undefined}
        onChange={website => {
          void onChangeRow(row.rowNumber, { website });
        }}
      >
        <Stack gap="sm">
          {row.websiteCandidates.map(candidate => (
            <Radio key={candidate.website} value={candidate.website}>
              {[websiteLabel(candidate.website), candidate.companyName, candidate.country].filter(Boolean).join(' · ')}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      {refusedPick !== undefined && pending === undefined && (
        <div role="alert">
          <Text size="sm" color="red-500">
            {websiteLabel(refusedPick.website)}: {refusalMessage(refusedPick.refusal)}
          </Text>
        </div>
      )}
      {isTyping ? (
        <TypedWebsite
          {...props}
          submitLabel="Add website"
          onCancel={() => {
            setIsTyping(false);
          }}
        />
      ) : (
        <Text size="sm">
          <Link
            isDisabled={pending !== undefined}
            onPress={() => {
              setIsTyping(true);
            }}
          >
            Type another website
          </Link>
        </Text>
      )}
    </Stack>
  );
}

/**
 * A website the user types for a row. A refused one keeps its message under the field for as long as
 * the field holds it, and starts the field when the card is drawn again.
 */
function TypedWebsite({
  row,
  refusedWebsites,
  pendingRows,
  onChangeRow,
  submitLabel,
  initialWebsite,
  onCancel,
  onSaved,
}: SupplierImportReviewViewProps & {
  row: SupplierImportReviewRow;
  submitLabel: string;
  initialWebsite?: string;
  onCancel?: () => void;
  onSaved?: () => void;
}) {
  const refused = refusedWebsites.get(row.rowNumber);
  const [draft, setDraft] = useState(refused?.website ?? initialWebsite ?? '');
  const isPending = pendingRows.has(row.rowNumber);
  const website = draft.trim();
  const error = refused !== undefined && refused.website === website ? refusalMessage(refused.refusal) : undefined;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (website === '' || isPending) return;
    if (await onChangeRow(row.rowNumber, { website })) onSaved?.();
  }

  return (
    <form
      className={styles.websiteForm}
      onSubmit={event => {
        void submit(event);
      }}
    >
      <div className={styles.websiteInput}>
        <Input
          label={`Website for ${rowLabel(row)}`}
          isLabelVisuallyHidden
          placeholder="example.com"
          value={draft}
          onChange={setDraft}
          isDisabled={isPending}
          error={error}
        />
      </div>
      {onCancel && (
        <Button variant="text" size="small" isDisabled={isPending} onPress={onCancel}>
          Cancel
        </Button>
      )}
      <Button type="submit" variant="outlined" size="small" isPending={isPending} isDisabled={website === ''}>
        {submitLabel}
      </Button>
    </form>
  );
}
